import { memo } from "react";

import MuiAutocomplete from "@mui/material/Autocomplete";
import { styled, Theme } from "@mui/material/styles";
import { CSSProperties } from "@mui/styles/withStyles";

import { InternalClasses } from "../helpers";

import { InputVibe } from "@/types/mui";

export type BaseAutocompleteBaseClasses =
  | "endAdornment"
  | "clearIndicator"
  | "popupIndicator"
  | "disablePopupIndicatorTransform"
  | "indicator"
  | "iconForm"
  | "option"
  | "optionForm"
  | "paper"
  | "input"
  | "inputWithHiddenTags"
  | "listBox"
  | "root"
  | "inputRoot"
  | "inputRootWithHiddenTags";

const optionVibe = (vibe: InputVibe, theme: Theme): CSSProperties => ({
  color: theme.palette.text[vibe].moderate,
  fill: theme.palette.text[vibe].moderate,
});

const optionHover = (vibe: InputVibe, theme: Theme): CSSProperties => ({
  background: theme.palette.surface[vibe].supershy,
  backgroundColor: theme.palette.surface[vibe].supershy,
});

const PREFIX = "ZeffyAutocomplete";

export const zeffyAutocompleteClasses: InternalClasses<typeof PREFIX, BaseAutocompleteBaseClasses> = {
  endAdornment: `${PREFIX}-endAdornment`,
  clearIndicator: `${PREFIX}-clearIndicator`,
  popupIndicator: `${PREFIX}-popupIndicator`,
  disablePopupIndicatorTransform: `${PREFIX}-disablePopupIndicatorTransform`,
  indicator: `${PREFIX}-indicator`,
  iconForm: `${PREFIX}-iconForm`,
  option: `${PREFIX}-option`,
  optionForm: `${PREFIX}-optionForm`,
  paper: `${PREFIX}-paper`,
  input: `${PREFIX}-input`,
  inputWithHiddenTags: `${PREFIX}-inputWithHiddenTags`,
  inputRoot: `${PREFIX}-inputRoot`,
  inputRootWithHiddenTags: `${PREFIX}-inputRootWithHiddenTags`,
  root: `${PREFIX}-root`,
  listBox: `${PREFIX}-listBox`,
};

export const StyledAutocomplete = memo(
  styled(MuiAutocomplete)(({ theme }: { theme: Theme }) => ({
    [`& .${zeffyAutocompleteClasses.endAdornment}`]: {
      position: "static",
      top: "unset",
      right: "unset",
      bottom: "unset",
      left: "unset",
      display: "flex",
      padding: 0,
      transform: "none",
    },

    [`& .${zeffyAutocompleteClasses.clearIndicator}`]: {
      margin: 0,
      transition: "all 200ms ease-in",
      opacity: 1,
      transform: "scale(1)",
      animation: "clearIndicatorAppear 200ms ease-in",
      "@keyframes clearIndicatorAppear": {
        from: {
          opacity: 0,
          transform: "scale(0) rotate(-90deg)",
        },
        to: {
          opacity: 1,
          transform: "scale(1) rotate(0deg)",
        },
      },
    },

    [`& .${zeffyAutocompleteClasses.popupIndicator}`]: {
      padding: theme.spacing(0.5),
      flexShrink: 0,
    },

    [`& .${zeffyAutocompleteClasses.disablePopupIndicatorTransform}`]: {
      transform: "none",
    },

    [`& .${zeffyAutocompleteClasses.indicator}`]: {
      borderRadius: theme.radius(0.75),
    },

    [`& .${zeffyAutocompleteClasses.iconForm}`]: {
      fill: theme.palette.text.form.quiet,
      color: theme.palette.text.form.quiet,
    },

    [`& .${zeffyAutocompleteClasses.option}`]: {
      ...theme.typography.body2,
      ...optionVibe("neutral", theme),
      padding: theme.spacing(1, 2),
      overflow: "hidden",
      "&[aria-selected='true']": {
        ...theme.typography.subtitle2,
        background: theme.palette.primary.lightest,
      },
      "&.Mui-focused": optionHover("neutral", theme),
    },

    [`& .${zeffyAutocompleteClasses.optionForm}`]: {
      ...optionVibe("form", theme),
      "&.Mui-focused": optionHover("form", theme),
    },

    [`& .${zeffyAutocompleteClasses.paper}`]: {
      boxShadow: theme.boxShadow.base.mid,
    },

    [`& .${zeffyAutocompleteClasses.input}`]: {
      flexShrink: 0,
      width: 10,
    },

    [`& .${zeffyAutocompleteClasses.inputWithHiddenTags}`]: {
      marginLeft: 0,
      paddingLeft: theme.spacing(1),
    },

    [`& .${zeffyAutocompleteClasses.inputRoot}`]: {
      overflow: "hidden",
      display: "flex",
      flexWrap: "nowrap",
    },

    [`& .${zeffyAutocompleteClasses.inputRootWithHiddenTags}`]: {
      paddingLeft: 0,
    },

    [`&.${zeffyAutocompleteClasses.root}`]: {},

    [`& .${zeffyAutocompleteClasses.listBox}`]: {
      maxHeight: 200,
    },
  }))
) as typeof MuiAutocomplete;

export const checkboxSx = (theme: Theme) => ({
  marginRight: theme.spacing(1),
});
